import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

/**
 * Beta Banner Component
 * Displays a dismissible banner at the top of the site indicating beta status
 * Can be easily removed by not importing/rendering this component after launch
 */
const BetaBanner = () => {
  const [isVisible, setIsVisible] = useState(() => {
    // Check if user has dismissed the banner
    const dismissed = localStorage.getItem('beta_banner_dismissed');
    return !dismissed;
  });

  const handleDismiss = () => {
    localStorage.setItem('beta_banner_dismissed', 'true');
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white overflow-hidden"
        >
          <div className="max-w-7xl mx-auto px-4 py-2 sm:py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                {/* Beta Badge */}
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-sm">
                  🚀 BETA
                </span>

                {/* Message */}
                <p className="text-sm sm:text-base font-medium">
                  <span className="hidden sm:inline">
                    Boop is in beta! We're actively building and improving. Your feedback helps us grow.
                  </span>
                  <span className="sm:hidden">
                    Beta version - actively improving!
                  </span>
                </p>

                {/* Beta Info Link */}
                <Link
                  to="/beta"
                  className="hidden md:inline-flex items-center gap-1 px-3 py-1 text-sm font-medium bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <span>Learn More</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Link>
              </div>

              {/* Dismiss Button */}
              <button
                onClick={handleDismiss}
                className="flex-shrink-0 p-1 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Dismiss banner"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BetaBanner;
